/**
 * trustMatrix — declarative table mapping user actions to the trust
 * requirements that gate them.
 *
 * Each row evaluates against the TrustState produced by useTrustState
 * and yields one of three states:
 *   - "allowed"     all preconditions met
 *   - "restricted"  at least one precondition unmet (matrix shows why)
 *   - "blocked"     a hard block applies regardless of preconditions
 *                   (e.g. high recent risk → matching paused)
 *
 * Reasons are written for members. Each reason carries an optional
 * remediation `cta` so the matrix can render an action button next to
 * the row.
 */
import type { TrustState } from "./useTrustState";

export type ActionStatus = "allowed" | "restricted" | "blocked";

export type RemediationKey =
  | "verify_identity"
  | "add_legal_name"
  | "confirm_email"
  | "complete_profile"
  | "add_emergency_contact"
  | "acknowledge_emergency"
  | "upload_more_photos"
  | "upgrade_tier"
  | "wait_for_review"
  | "contact_support";

export interface MatrixReason {
  text: string;
  cta?: RemediationKey;
}

export interface MatrixRow {
  id: string;
  category: "messaging" | "matching" | "events" | "voice_video" | "discovery";
  label: string;
  description: string;
  evaluate: (s: TrustState) => {
    status: ActionStatus;
    reasons: MatrixReason[];
  };
}

const r = (text: string, cta?: RemediationKey): MatrixReason => ({ text, cta });

export const TRUST_MATRIX: MatrixRow[] = [
  {
    id: "send_first_message",
    category: "messaging",
    label: "Send a first message",
    description:
      "Open a new conversation after a mutual match.",
    evaluate: (s) => {
      const reasons: MatrixReason[] = [];
      if (s.riskLevel === "high")
        return {
          status: "blocked",
          reasons: [
            r(
              "Messaging is paused while our trust & safety team reviews recent activity on your account.",
              "wait_for_review",
            ),
          ],
        };
      if (!s.kycVerified)
        reasons.push(r("Verify your ID to unlock new conversations.", "verify_identity"));
      if (!s.emailConfirmed)
        reasons.push(r("Confirm your email address.", "confirm_email"));
      return { status: reasons.length ? "restricted" : "allowed", reasons };
    },
  },
  {
    id: "share_media_in_chat",
    category: "messaging",
    label: "Share photos & voice notes",
    description: "Attach media inside an active conversation.",
    evaluate: (s) => {
      const reasons: MatrixReason[] = [];
      if (s.riskLevel !== "low")
        reasons.push(
          r(
            s.riskLevel === "high"
              ? "Media sharing is paused while we review your account."
              : "Media sharing is limited because we recently flagged unusual activity. It re-opens automatically.",
            s.riskLevel === "high" ? "wait_for_review" : undefined,
          ),
        );
      if (!s.kycVerified)
        reasons.push(r("Verify your ID to send photos and voice notes.", "verify_identity"));
      return {
        status:
          s.riskLevel === "high"
            ? "blocked"
            : reasons.length
              ? "restricted"
              : "allowed",
        reasons,
      };
    },
  },
  {
    id: "appear_in_discovery",
    category: "discovery",
    label: "Appear in Discovery",
    description: "Be shown to other members in the swipe & map deck.",
    evaluate: (s) => {
      const reasons: MatrixReason[] = [];
      if (!s.profileComplete)
        reasons.push(
          r(
            "Add a display name, bio, 3+ photos, and 3+ prompts so members have something to react to.",
            "complete_profile",
          ),
        );
      if (!s.hasMinimumPhotos)
        reasons.push(r("Add at least 3 photos.", "upload_more_photos"));
      if (!s.kycVerified)
        reasons.push(
          r(
            "Unverified profiles get reduced reach. Verify ID for full Discovery surface.",
            "verify_identity",
          ),
        );
      return { status: reasons.length ? "restricted" : "allowed", reasons };
    },
  },
  {
    id: "match_with_verified",
    category: "matching",
    label: "Match with verified members",
    description:
      "Be eligible to appear for members who only see ID-verified profiles.",
    evaluate: (s) => {
      if (!s.kycVerified)
        return {
          status: "restricted",
          reasons: [
            r(
              "ID-verified members can hide unverified profiles by default. Verify to be visible to them.",
              "verify_identity",
            ),
          ],
        };
      return { status: "allowed", reasons: [] };
    },
  },
  {
    id: "rsvp_event",
    category: "events",
    label: "RSVP to events",
    description:
      "Reserve a spot at member events. Most events require ID verification.",
    evaluate: (s) => {
      const reasons: MatrixReason[] = [];
      if (!s.kycVerified)
        reasons.push(r("Most events require a verified ID.", "verify_identity"));
      if (!s.hasLegalName)
        reasons.push(
          r("We need your legal name on file for event check-in.", "add_legal_name"),
        );
      return { status: reasons.length ? "restricted" : "allowed", reasons };
    },
  },
  {
    id: "voice_video_call",
    category: "voice_video",
    label: "In-app voice & video calls",
    description: "Start a call inside an active conversation.",
    evaluate: (s) => {
      if (s.riskLevel === "high")
        return {
          status: "blocked",
          reasons: [
            r(
              "Calling is paused while our trust & safety team reviews your account.",
              "wait_for_review",
            ),
          ],
        };
      const reasons: MatrixReason[] = [];
      if (!s.kycVerified)
        reasons.push(
          r("Verify your ID to start in-app calls.", "verify_identity"),
        );
      if (!s.hasEmergencyContact)
        reasons.push(
          r(
            "Add an emergency contact so the Safety Check-in flow works during a call.",
            "add_emergency_contact",
          ),
        );
      return { status: reasons.length ? "restricted" : "allowed", reasons };
    },
  },
  {
    id: "host_event",
    category: "events",
    label: "Host a community event",
    description: "Create and run an in-person event for members.",
    evaluate: (s) => {
      const reasons: MatrixReason[] = [];
      if (!s.kycVerified)
        reasons.push(r("Hosts must be ID-verified.", "verify_identity"));
      if (!s.bgcVerified)
        reasons.push(
          r(
            "Hosting requires a Background Check. Start one from the ID verification panel.",
            "verify_identity",
          ),
        );
      if (!s.communityVerified)
        reasons.push(
          r(
            "Community verification is granted by our team after your first event reference. Apply from this page once everything else is green.",
            "contact_support",
          ),
        );
      return {
        status: !s.kycVerified || !s.bgcVerified ? "restricted" : reasons.length ? "restricted" : "allowed",
        reasons,
      };
    },
  },
  {
    id: "boost_visibility",
    category: "discovery",
    label: "Use Amplify, Priority Match, Replays",
    description: "Spend à la carte boosts to increase reach or revisit profiles.",
    evaluate: (s) => {
      if (!s.kycVerified)
        return {
          status: "restricted",
          reasons: [
            r(
              "Paid boosts require ID verification — keeps the deck honest for everyone.",
              "verify_identity",
            ),
          ],
        };
      return { status: "allowed", reasons: [] };
    },
  },
];

export const REMEDIATION: Record<
  RemediationKey,
  { label: string; href?: string; section?: string }
> = {
  verify_identity: { label: "Verify ID", section: "kyc" },
  add_legal_name: { label: "Add legal name", section: "legal-name" },
  confirm_email: { label: "Resend confirmation", section: "email" },
  complete_profile: { label: "Complete profile", href: "/profile" },
  add_emergency_contact: { label: "Add emergency contact", section: "emergency" },
  acknowledge_emergency: { label: "Acknowledge", section: "emergency" },
  upload_more_photos: { label: "Add photos", href: "/profile" },
  upgrade_tier: { label: "Upgrade plan", href: "/pricing" },
  wait_for_review: { label: "Learn more", href: "/safety" },
  contact_support: { label: "Contact support", href: "/help" },
};
